import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  output
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import {
  ITroubleshootingForm,
  IWindscreenForm
} from "src/app/interfaces/interfaces";
import { FormService } from "src/app/services/form.service";
import { MappingService } from "src/app/services/mapping.service";
import { PagingService } from "src/app/services/paging.service";
import { PriceService } from "src/app/services/price.service";
import { UrlService } from "src/app/services/url.service";
import {
  PackageResponse,
  PackageTagEnum,
  PackageTypeEnum,
  PriceTypeEnum,
  WorkshopSystemEnum,
  DiscountTypeEnum,
  VehicleSearchResponse,
  ActiveWorkshopSystemResponse,
  DealerResponse,
  CampaignResponse,
  SelectedPackagesRequest
} from "src/domain/client";
import { RouterLink } from "@angular/router";
import { WorkshopInfoComponent } from "../workshop-info/workshop-info.component";
import { PackageSummaryComponent } from "../package-summary/package-summary.component";
import { MatDivider } from "@angular/material/divider";
import { BrandCardComponent } from "../brand-card/brand-card.component";
import { PricePipe } from "../../../pipes/sharedpipes/price.pipe";
import { FloorPipe } from "../../../pipes/sharedpipes/floor.pipe";
import { TranslateModule } from "@ngx-translate/core";
import { CarBrandPipe } from "../../../pipes/car-brand.pipe";
import { PackagePricePipe } from "../../../pipes/package-price.pipe";
import { TotalPricePipe } from "../../../pipes/total-price.pipe";
import { Store } from "@ngrx/store";
import {
  BookingState,
  selectActiveWorkshopSystems,
  selectAdditionalPackages,
  selectAllSelectedPackagesForRequest,
  selectAreAllRequiredPackagesSelected,
  selectAvailableTimes,
  selectBasePackages,
  selectBookingState,
  selectSelectedDealer,
  selectSelectedAdditionalPackagesForRequest,
  selectSelectedBasePackagesForRequest,
  selectSelectedDropoffPackage,
  selectCampaignResponse,
  selectSelectedWarningLights,
  selectInsuranceProviderName,
  selectSelectedEbuCar
} from "src/app/state";
import { EbuCarModelResponse } from "src/domain/client";

@Component({
  selector: "app-summary",
  templateUrl: "./summary.component.html",
  imports: [
    RouterLink,
    WorkshopInfoComponent,
    PackageSummaryComponent,
    MatDivider,
    BrandCardComponent,
    PricePipe,
    FloorPipe,
    TranslateModule,
    CarBrandPipe,
    PackagePricePipe,
    TotalPricePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SummaryComponent {
  public WorkshopSystemEnum: typeof WorkshopSystemEnum = WorkshopSystemEnum;
  public PackageTypeEnum: typeof PackageTypeEnum = PackageTypeEnum;
  public PriceTypeEnum: typeof PriceTypeEnum = PriceTypeEnum;
  public PackageTagEnum: typeof PackageTagEnum = PackageTagEnum;

  navigation = output<void>();

  public DiscountTypeEnum = DiscountTypeEnum;

  readonly selectedBasePackagesForRequest: Signal<
    SelectedPackagesRequest[] | null
  >;
  readonly selectedAdditionalPackagesForRequest: Signal<
    SelectedPackagesRequest[] | null
  >;
  readonly selectedAllPackagesForRequest: Signal<
    SelectedPackagesRequest[] | null
  >;
  readonly selectedWorkshop: Signal<DealerResponse | null>;
  readonly booking: Signal<BookingState | null>;

  readonly basePackages: Signal<PackageResponse[] | null>;
  readonly selectedDropoffPackage: Signal<PackageResponse | null>;
  readonly additionalPackages: Signal<PackageResponse[] | null>;
  readonly carInfo: Signal<VehicleSearchResponse | null>;
  readonly activeWorkshopSystems: Signal<ActiveWorkshopSystemResponse[] | null>;
  readonly areAllRequiredPackagesSelected: Signal<boolean>;
  readonly campaignResponse: Signal<CampaignResponse | null>;
  readonly selectedWarningLights: Signal<any[]>;
  readonly availableTimes: Signal<any[] | null>;
  readonly insuranceProviderName: Signal<string | null>;
  readonly selectedEbuCar: Signal<EbuCarModelResponse | null>;

  constructor(
    public formService: FormService,
    public pagingService: PagingService,
    public urlService: UrlService,
    public mappingService: MappingService,
    public priceService: PriceService,
    private store: Store
  ) {
    this.booking = this.store.selectSignal(selectBookingState);
    this.selectedEbuCar = this.store.selectSignal(selectSelectedEbuCar);

    this.selectedBasePackagesForRequest = this.store.selectSignal(
      selectSelectedBasePackagesForRequest
    );
    this.selectedAdditionalPackagesForRequest = this.store.selectSignal(
      selectSelectedAdditionalPackagesForRequest
    );
    this.selectedAllPackagesForRequest = this.store.selectSignal(
      selectAllSelectedPackagesForRequest
    );
    this.selectedWorkshop = this.store.selectSignal(selectSelectedDealer);
    this.activeWorkshopSystems = this.store.selectSignal(
      selectActiveWorkshopSystems
    );

    this.selectedDropoffPackage = this.store.selectSignal(
      selectSelectedDropoffPackage
    );

    this.basePackages = this.store.selectSignal(selectBasePackages);

    this.additionalPackages = this.store.selectSignal(selectAdditionalPackages);

    this.areAllRequiredPackagesSelected = this.store.selectSignal(
      selectAreAllRequiredPackagesSelected
    );

    this.campaignResponse = this.store.selectSignal(selectCampaignResponse);

    this.selectedWarningLights = this.store.selectSignal(
      selectSelectedWarningLights
    );

    this.availableTimes = this.store.selectSignal(selectAvailableTimes);

    this.insuranceProviderName = this.store.selectSignal(
      selectInsuranceProviderName
    );
  }

  onNavigation(): void {
    // TODO: The 'emit' function requires a mandatory void argument
    this.navigation.emit();
  }

  momentToDateString(date: any): string {
    return date.format("YYYY-MM-DD");
  }

  get selectedDate() {
    return new Date(this.booking().selectedDate)?.toLocaleDateString("sv-SE");
  }

  get selectedTime() {
    return this.booking().selectedTime?.substring(0, 5);
  }

  get contactInformation() {
    return this.booking().contactInformation;
  }

  get showAdditionalPackages(): boolean {
    return (
      this.additionalPackages() &&
      this.selectedAdditionalPackagesForRequest().length > 0
    );
  }

  get troubleshootingForm(): FormGroup<ITroubleshootingForm> {
    return (
      this.formService.form.controls.chooseSpecificationsForm as FormGroup<any>
    ).controls.troubleshootingForm as FormGroup<ITroubleshootingForm>;
  }
  get windscreenForm(): FormGroup<IWindscreenForm> {
    return this.formService.form.controls.chooseSpecificationsForm.get(
      "windscreenForm"
    ) as FormGroup<IWindscreenForm>;
  }

  selectedMainPackages(type: PackageTypeEnum): PackageResponse[] {
    const selected = this.selectedBasePackagesForRequest();
    return (
      this.basePackages()
        ?.filter((x) => !!x)
        .filter(
          (pkg) =>
            pkg.type === type && !!selected.find((v) => v.packageId === +pkg.id)
        ) ?? []
    );
  }

  selectedBasePackages(pkg: PackageResponse): PackageResponse[] {
    const selected = this.selectedBasePackagesForRequest().find(
      (j) => j.packageId === +pkg.id
    );
    if (!selected) return [];
    const selectedPackage = this.basePackages()?.filter(
      (p) => +p.id == selected.packageId
    );
    if (!selectedPackage) return [];
    return selectedPackage;
  }

  getSelectedPackagesByType(type: PackageTypeEnum): PackageResponse[] {
    const selected = this.selectedAllPackagesForRequest();
    if (!this.basePackages()?.length) return [];
    const base =
      this.basePackages()
        ?.filter((x) => !!x)
        .filter(
          (pkg) =>
            pkg.type === type &&
            !!selected.find(
              (v) =>
                v.packageId === +pkg.id ||
                v.packageExternalId === pkg.packageExternalId
            )
        ) ?? [];
    const additional =
      this.additionalPackages()?.filter(
        (pkg) =>
          pkg.type === type &&
          !!selected.find(
            (v) =>
              v.packageId === +pkg.id ||
              v.packageExternalId === pkg.packageExternalId
          )
      ) ?? [];
    return base.concat(additional);
  }

  hasAllSelected(pkg: PackageResponse): boolean {
    if (
      pkg.subPackages.filter((p) => !p.tags.includes(PackageTagEnum.Removable))
        .length >= pkg.subPackages.length
    )
      return true;
    return (
      !!pkg.subPackages.length &&
      this.selectedBasePackagesForRequest().find((p) => p.packageId === +pkg.id)
        .subPackageIds.length ===
        pkg.subPackages.length - 1
    );
  }

  hasAsteriskPrice(): boolean {
    return PackagePricePipe.hasAsteriskPrice(
      this.selectedAllPackagesForRequest(),
      this.basePackages(),
      this.additionalPackages()
    );
  }
}
